import { Grid, Hidden } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { classNames } from 'react-extras';
import { useSelector } from 'react-redux';

import WxCopilotHost from 'components/WxCopilotHost';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { debounce } from 'utils/debounce';

import AnalyticsBetaNavLink from './components/AnalyticsBetaNavLink';
import AnalyticsNavLink from './components/AnalyticsNavLink';
import ApplicantsNavMenu from './components/ApplicantsNavMenu';
import CalendarNavLink from './components/CalendarNavLink';
import GlobalNavLogo from './components/GlobalNavLogo';
import GlobalNavMobile from './components/GlobalNavMobile';
import HelpMenu from './components/HelpMenu';
import JobsNavMenu from './components/JobsNavMenu';
import Messaging from './components/Messaging';
import MoreNavMenu from './components/MoreNavMenu';
import Notification from './components/Notification';
import PosthireNavLink from './components/PosthireNavLink';
import ProfileMenu from './components/ProfileMenu';
import Search from './components/Search';
import SourcingNavLink from './components/SourcingNavLink';
import useStyles from './styles';

const GlobalNav = ({ handleQueryChange, query, funnelExternalId }) => {
  const whoami = useSelector(makeSelectWhoami());

  const {
    analytics_enabled: analyticsEnabled,
    posthire_enabled: posthireEnabled,
    looker_analytics_enabled: lookerAnalyticsEnabled,
  } = whoami;

  const classes = useStyles();
  const desktopNav = createRef();
  const [invisible, setInvisible] = useState(true);
  const [moreDropdownVisibility, setMoreDropdownVisibility] = useState(0);

  const handleResize = useCallback(() => {
    if (!desktopNav.current) return;

    const desktopNavWidth = desktopNav.current.scrollWidth;
    const { parentElement } = desktopNav.current;
    const containerWidth = parentElement
      ? parentElement.clientWidth
      : window.innerWidth;

    if (moreDropdownVisibility < 3 && desktopNavWidth > containerWidth) {
      setMoreDropdownVisibility(moreDropdownVisibility + 1);
    }

    setTimeout(() => {
      setInvisible(false);
    }, 200);
  }, [desktopNav, moreDropdownVisibility]);

  // Re-measures after each collapse increment so the More menu converges until the row fits.
  const debouncedResize = useMemo(
    () => debounce(handleResize, 200),
    [handleResize],
  );

  useEffect(() => {
    // Initial calculation - handleResize checks for desktopNav.current internally
    handleResize();

    // Ensure invisible is set to false even if initial calculation fails
    const fallbackTimer = setTimeout(() => {
      setInvisible(false);
    }, 200);

    // Wait for ref to be available before setting up ResizeObserver
    if (!desktopNav.current) {
      // Fallback to window resize if ref not available yet
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(fallbackTimer);
        window.removeEventListener('resize', handleResize);
      };
    }

    // Use ResizeObserver to watch for parent container size changes
    // This automatically accounts for wx_navbar width and other layout changes
    const { parentElement } = desktopNav.current;
    if (!parentElement) {
      // Fallback to window resize if no parent element
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(fallbackTimer);
        window.removeEventListener('resize', handleResize);
      };
    }

    // Watch parent container for size changes
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(parentElement);

    // Also listen to window resize as fallback
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(fallbackTimer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [desktopNav, handleResize]);

  useEffect(() => {
    debouncedResize();
    return () => debouncedResize.clear();
  }, [moreDropdownVisibility, debouncedResize]);

  return (
    <>
      {/* MOBILE */}
      <Hidden mdUp>
        <GlobalNavMobile handleQueryChange={handleQueryChange} query={query} />
      </Hidden>

      {/* DESKTOP */}
      <Hidden smDown>
        <Grid
          container
          item
          direction="row"
          wrap="nowrap"
          alignItems="center"
          className={classes.container}
          ref={desktopNav}
        >
          {/* LOGO */}
          <GlobalNavLogo />

          {/* LEFT SECTION ITEMS */}
          <Grid
            className={classNames(classes.leftSection, {
              invisible,
            })}
            item
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            data-testid="navbar-main_menu"
          >
            <JobsNavMenu />

            <ApplicantsNavMenu funnelExternalId={funnelExternalId} />

            <CalendarNavLink />

            {moreDropdownVisibility < 2 && <SourcingNavLink />}

            {!moreDropdownVisibility && (
              <>
                {lookerAnalyticsEnabled && (
                  <AnalyticsNavLink permission={analyticsEnabled} />
                )}
                {analyticsEnabled && (
                  <AnalyticsBetaNavLink
                    permission={analyticsEnabled}
                    lookerAnalyticsEnabled={lookerAnalyticsEnabled}
                  />
                )}
                <PosthireNavLink permission={posthireEnabled} />
              </>
            )}

            {moreDropdownVisibility > 0 && (
              <MoreNavMenu
                viewAnalytics={
                  lookerAnalyticsEnabled &&
                  analyticsEnabled &&
                  moreDropdownVisibility > 0
                }
                viewOmniAnalytics={
                  analyticsEnabled && moreDropdownVisibility > 0
                }
                viewPosthire={posthireEnabled && moreDropdownVisibility > 0}
                viewSourcing={moreDropdownVisibility > 1}
                lookerAnalyticsEnabled={lookerAnalyticsEnabled}
              />
            )}
          </Grid>

          {/* RIGHT SECTION ITEMS */}
          <Grid
            className={classNames(classes.rightSection, {
              invisible,
            })}
            item
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
          >
            <Search handleQueryChange={handleQueryChange} query={query} />
            <Messaging />
            <HelpMenu copilotEnabled />
            <ProfileMenu />
            <>
              {/*
                In-flow mount target for the trigger UMD instance. Sits
                between ProfileMenu and the right edge of the nav so the
                icon scrolls/positions with GlobalNav itself rather than
                floating over the viewport.

                Explicit pixel sizing matters: the wx-system UMD attaches
                a Shadow DOM to this host, and recruiter-ui ships
                Material-UI/JSS (no Tailwind in the light tree), so
                Tailwind utility classes from the wx-system bundle don't
                affect this host element. Without a sized host, the icon
                inside the shadow root has no visible box.

                The "Ask Cue" trigger is a pill (icon + label), so the host
                height is fixed but its width is intrinsic — the pill inside
                the shadow root sizes itself. Clamping the host width here
                would clip the label.

                The legacy `classes.copilotItem` wrapper (display: none
                unless an `onButtonEvent` raised a visibility flag) is
                deliberately gone — the trigger is unconditionally
                visible.
              */}
              <div
                id="wx-cue-trigger"
                style={{
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              />
              <WxCopilotHost
                mode="trigger"
                openMethod="nav_bar"
                buttonClassName="h-10"
              />
            </>
          </Grid>
        </Grid>
        <Grid>
          <Notification />
        </Grid>
      </Hidden>
    </>
  );
};

GlobalNav.propTypes = {
  handleQueryChange: PropTypes.func,
  query: PropTypes.string,
  funnelExternalId: PropTypes.string,
};

export default GlobalNav;
